interface Bullet {
  heading: string;
  text: string;
}

interface Props {
  question: string;
  answer: string;
  label?: string;
  bullets?: Bullet[];
}

export default function SectionAIAnswer({ question, answer, label = 'AI Answer Block', bullets }: Props) {
  return (
    <section className="relative bg-[#050a12] py-24 overflow-hidden">
      {/* Background accent */}
      <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '28px 28px' }} />
      <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, #c9a84c55, transparent)' }} />
      <div className="absolute bottom-0 left-0 right-0 h-[1px]" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)' }} />

      {/* Large background glow */}
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-[0.04]" style={{ background: 'radial-gradient(circle, #c9a84c, transparent)' }} />

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">

          {/* Left label panel */}
          <div className="lg:col-span-3">
            <div className="sticky top-32">
              <div className="inline-flex items-center gap-2.5 mb-5">
                <div className="w-9 h-9 flex items-center justify-center bg-[#0ea5e9]/10 border border-[#0ea5e9]/20 rounded-xl">
                  <i className="ri-robot-2-line text-[#0ea5e9] text-base" />
                </div>
                <div>
                  <p className="text-[#c9a84c] text-[10px] font-bold uppercase tracking-[0.3em]">{label}</p>
                  <p className="text-white/30 text-[10px]">AI &amp; Voice Optimized</p>
                </div>
              </div>

              <div className="mt-6 space-y-2.5">
                {['AI Extractable', 'Voice Search Ready', 'Schema Ready', '40–60 Words'].map((tag) => (
                  <div key={tag} className="flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-check-line text-[#c9a84c] text-xs" />
                    </div>
                    <span className="text-white/35 text-xs">{tag}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right content panel */}
          <div className="lg:col-span-9">
            {/* Question */}
            <div className="mb-8">
              <h2
                className="text-white font-bold leading-tight mb-0"
                style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(26px, 3.2vw, 42px)' }}
              >
                {question}
              </h2>
            </div>

            {/* Answer box */}
            <div className="relative bg-white/4 border border-white/10 rounded-2xl p-7 mb-6 hover:border-white/15 transition-border duration-300">
              {/* Blue left accent */}
              <div className="absolute left-0 top-6 bottom-6 w-[3px] bg-gradient-to-b from-[#0ea5e9] to-[#0ea5e9]/20 rounded-full" />
              <p className="text-white/75 text-lg leading-[1.9] pl-4">{answer}</p>
            </div>

            {/* Optional bullets */}
            {bullets && bullets.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {bullets.map((b) => (
                  <div key={b.heading} className="bg-white/3 border border-white/8 rounded-xl p-5 hover:border-[#c9a84c]/25 transition-colors duration-300">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#c9a84c]" />
                      <h3 className="text-[#c9a84c] text-xs font-bold uppercase tracking-wider">{b.heading}</h3>
                    </div>
                    <p className="text-white/55 text-sm leading-relaxed">{b.text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
